import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef } from 'react';
import { serviceItems } from '../content';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

gsap.registerPlugin(ScrollTrigger);

export function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLParagraphElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;

    if (!section || !track) {
      return;
    }

    if (prefersReducedMotion) {
      return;
    }

    const getScrollAmount = () => Math.max(track.scrollWidth - section.offsetWidth + 64, 0);

    const animation = gsap.to(track, {
      x: () => -getScrollAmount(),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${getScrollAmount() + window.innerHeight * 0.35}`,
        pin: true,
        scrub: 0.75,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (!cueRef.current) {
            return;
          }

          cueRef.current.style.opacity = String(Math.max(0, 1 - self.progress * 3));
        },
      },
    });

    return () => {
      animation.scrollTrigger?.kill();
      animation.kill();
    };
  }, [prefersReducedMotion]);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative overflow-hidden bg-paper text-ink"
      aria-labelledby="services-heading"
    >
      <div className="px-[var(--section-padding-x)] pt-[calc(var(--section-padding-y)*0.55)]">
        <p className="section-eyebrow">Services</p>
        <h2 id="services-heading" className="section-title">
          What happens in the room
        </h2>
        <p className="section-copy max-w-lg">
          A straight view of the care Bryanpark delivers day to day.
        </p>
      </div>

      <div className={`pb-[var(--section-padding-y)] pt-14 ${prefersReducedMotion ? '' : 'min-h-[70vh]'}`}>
        <div
          ref={trackRef}
          className={`flex px-[var(--section-padding-x)] ${
            prefersReducedMotion ? 'flex-col gap-10' : 'w-max gap-0'
          }`}
        >
          {serviceItems.map((service, index) => (
            <article
              key={service.title}
              className={`shrink-0 border-neutral-200 ${
                prefersReducedMotion
                  ? 'w-full border-t pt-8'
                  : 'flex h-[38vh] max-h-[24rem] w-[min(78vw,28rem)] flex-col justify-end border-l px-8 py-7 lg:w-[30rem]'
              }`}
            >
              <span className="font-body text-xs uppercase tracking-[0.28em] text-primary-600">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-5 font-display text-3xl font-semibold leading-tight text-ink sm:text-4xl">
                {service.title}
              </h3>
              <p className="mt-5 max-w-sm font-body text-base leading-7 text-neutral-600">{service.body}</p>
            </article>
          ))}
        </div>

        {!prefersReducedMotion && (
          <p
            ref={cueRef}
            className="pointer-events-none mt-8 px-[var(--section-padding-x)] font-body text-xs uppercase tracking-[0.28em] text-neutral-500"
            aria-hidden="true"
          >
            01 &rarr; {String(serviceItems.length).padStart(2, '0')}
          </p>
        )}
      </div>
    </section>
  );
}
